/**
 * Inline extraction details rendered below an expanded document row.
 *
 * Extracted from DocumentsTable.jsx in the Feb-25 refactor — same
 * behaviour, just lifted into its own file to keep DocumentsTable
 * focused on the table shell + filtering.
 *
 *   • InlineExtractionDetails — lazy-fetched plain-language extraction
 *                                 details rendered below the active row.
 *   • ExtractionField         — single key-value cell with plain-language
 *                                 confidence pill + quote.
 */
import React, { useEffect, useState } from "react";
import { Loader2, Quote } from "lucide-react";
import api from "../../../lib/api";
import {
  confidenceTier, plainDocKey, plainDocType, plainExtractionStatus,
  plainFieldName,
} from "../../../lib/plainLanguage";
import { fmtDate } from "./primitives";


export default function InlineExtractionDetails({ merchantId, doc }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const r = await api.get(`/admin/merchants/${merchantId}/cross-check/extractions/${doc.doc_id}`);
        if (alive) setData(r.data);
      } catch (e) {
        if (alive) setError(e?.response?.status === 404
          ? "No extraction recorded for this document."
          : "Couldn't load the extraction.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [merchantId, doc.doc_id]);

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-zinc-500 text-[12.5px]" data-testid={`extraction-loading-${doc.doc_id}`}>
        <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading details…
      </div>
    );
  }
  if (error) {
    return <div className="text-rose-300 text-[12.5px]" data-testid={`extraction-error-${doc.doc_id}`}>{error}</div>;
  }
  if (!data) return null;

  const fields = data.extracted_fields || {};
  const confs = data.field_confidences || {};
  const quotes = data.field_quotes || {};
  const statusInfo = plainExtractionStatus(data.extraction_status);
  const classifiedPlain = data.doc_type_classified ? plainDocType(data.doc_type_classified) : null;
  const expectedPlain = plainDocKey(doc.doc_key);
  const mismatch = data.doc_type_classified && data.doc_type_classified !== doc.doc_key
    && data.doc_type_classified !== "unknown";

  // Brief 3.10 — UBOs derived from a shareholders register / share
  // cert / ownership chart. Hide the two provenance fields from the
  // main grid and surface them as a styled banner above the fields.
  const ubosWereDerived = fields.ubos_were_derived === true;
  const derivationRule = fields.derivation_rule;
  const sourceDocForm = fields.source_doc_form;
  const HIDDEN_FIELDS = new Set(["ubos_were_derived", "derivation_rule"]);
  const visibleFields = Object.entries(fields).filter(([k]) => !HIDDEN_FIELDS.has(k));

  return (
    <div className="space-y-4" data-testid={`extraction-inline-${doc.doc_id}`}>
      {/* Plain-language headline */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <div className="text-[12.5px]">
          <span className="text-zinc-500 uppercase tracking-[0.18em] text-[10.5px] mr-2">Status</span>
          <span className={
            statusInfo.tone === "ok" ? "text-emerald-200" :
            statusInfo.tone === "warn" ? "text-amber-200" :
            statusInfo.tone === "bad" ? "text-rose-200" : "text-zinc-300"
          } data-testid={`extraction-status-label-${doc.doc_id}`}>
            {statusInfo.label}
          </span>
          {statusInfo.helper && (
            <span className="text-zinc-500 ml-2">{statusInfo.helper}</span>
          )}
        </div>
        {classifiedPlain && (
          <div className="text-[12.5px]" data-testid={`extraction-classified-${doc.doc_id}`}>
            <span className="text-zinc-500 uppercase tracking-[0.18em] text-[10.5px] mr-2">Nerea identified this as</span>
            <span className={mismatch ? "text-amber-200" : "text-zinc-200"}>{classifiedPlain}</span>
            {mismatch && (
              <span className="text-zinc-500 ml-2">— expected {expectedPlain}</span>
            )}
          </div>
        )}
      </div>

      {(data.warnings || []).length > 0 && (
        <div className="rounded-lg border border-amber-400/30 bg-amber-400/[0.06] px-3 py-2 text-[11.5px] text-amber-100" data-testid={`extraction-warnings-${doc.doc_id}`}>
          <div className="text-[10.5px] uppercase tracking-[0.18em] text-amber-200/80 mb-1">Warnings</div>
          <ul className="list-disc list-inside space-y-0.5">
            {(data.warnings || []).map((w, i) => <li key={i}>{w}</li>)}
          </ul>
        </div>
      )}

      {ubosWereDerived && (
        <div
          className="rounded-lg border border-violet-400/30 bg-violet-400/[0.06] px-3 py-2 text-[11.5px] text-violet-100"
          data-testid={`extraction-ubo-derivation-${doc.doc_id}`}
          title={derivationRule || "UBOs were computed from the shareholder list using the Cyprus / EU 25% rule."}
        >
          <div className="text-[10.5px] uppercase tracking-[0.18em] text-violet-200/80 mb-1">
            UBOs derived (Cyprus / EU 25% rule)
          </div>
          <div>
            {sourceDocForm
              ? <>Source: <span className="font-mono text-violet-100">{sourceDocForm.replace(/_/g, " ")}</span>. </>
              : null}
            {derivationRule || "Beneficial owners computed from the shareholder list."}
          </div>
        </div>
      )}

      <div>
        <div className="text-[10.5px] uppercase tracking-[0.18em] text-zinc-500 mb-2">
          Fields ({visibleFields.length})
        </div>
        {visibleFields.length === 0 ? (
          <div className="text-zinc-500 text-[12px]">No fields extracted.</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {visibleFields.map(([k, v]) => (
              <ExtractionField
                key={k}
                name={k}
                value={v}
                confidence={confs[k]}
                quote={quotes[k]}
              />
            ))}
          </div>
        )}
      </div>

      <div className="text-[11px] text-zinc-500 pt-1 border-t border-white/[0.04]">
        Extracted {fmtDate(data.extracted_at)}{data.model ? ` · model: ${data.model}` : ""}
      </div>
    </div>
  );
}


function ExtractionField({ name, value, confidence, quote }) {
  const tier = confidenceTier(confidence);
  const conf = typeof confidence === "number" ? confidence : null;
  const tierCls = !tier ? "" :
    tier.tone === "ok" ? "text-emerald-300 border-emerald-400/30 bg-emerald-400/[0.06]" :
    tier.tone === "warn" ? "text-amber-200 border-amber-400/30 bg-amber-400/[0.06]" :
    "text-rose-300 border-rose-400/30 bg-rose-400/[0.06]";
  return (
    <div className="rounded-lg border border-white/[0.06] bg-white/[0.015] px-3 py-2.5" data-testid={`extraction-field-${name}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-[11px] text-zinc-500" data-testid={`extraction-field-label-${name}`}>{plainFieldName(name)}</div>
          <div className="text-white text-[12.5px] mt-0.5 break-words">
            {value === null || value === undefined || value === ""
              ? <span className="text-zinc-600 italic">Not found</span>
              : typeof value === "object"
                ? <pre className="text-[11.5px] whitespace-pre-wrap font-mono text-zinc-300">{JSON.stringify(value, null, 2)}</pre>
                : String(value)}
          </div>
        </div>
        {tier && (
          <span
            className={`shrink-0 inline-flex items-center rounded-full border px-2 py-0.5 text-[10.5px] ${tierCls}`}
            title={`${tier.helper} (${(conf * 100).toFixed(0)}%)`}
            data-testid={`extraction-field-confidence-${name}`}
          >
            {tier.label} · {(conf * 100).toFixed(0)}%
          </span>
        )}
      </div>
      {quote?.quote && (
        <div className="mt-2 flex items-start gap-1.5 text-[11.5px] text-zinc-400 border-l border-white/[0.08] pl-2">
          <Quote className="h-3 w-3 mt-0.5 text-zinc-600 shrink-0" />
          <div className="italic">
            {`"${String(quote.quote).slice(0, 220)}${String(quote.quote).length > 220 ? "…" : ""}"`}
            {quote.page != null && <span className="text-zinc-600 not-italic ml-1">· p.{quote.page}</span>}
          </div>
        </div>
      )}
    </div>
  );
}
